import { useEffect, useState, Fragment } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader2, Tags, Plus, GripVertical, Pencil, Check, X, Trash2, Eye, EyeOff, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const KINDS = [["attraction", "Attractions"], ["advertiser", "Advertisers"], ["detour", "Detours"], ["route", "Routes"]];

function KindSection({ kind, label, rows, reload }) {
  const [items, setItems] = useState(rows);
  const [nv, setNv] = useState("");
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [delCat, setDelCat] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setItems(rows); }, [rows]);

  const add = async () => {
    const v = nv.trim();
    if (!v) return;
    setBusy(true);
    try { await api.post("/admin/categories", { kind, name: v }); setNv(""); await reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveEdit = async (id) => {
    const v = editVal.trim();
    if (!v) return;
    setBusy(true);
    try { await api.put(`/admin/categories/${kind}/${id}`, { name: v }); setEditId(null); await reload(); toast.success("Renamed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggleHidden = async (c) => {
    setItems((s) => s.map((x) => (x.id === c.id ? { ...x, hidden: !x.hidden } : x)));
    try { await api.put(`/admin/categories/${kind}/${c.id}`, { hidden: !c.hidden }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); reload(); }
  };
  const doDelete = async () => {
    const c = delCat; setDelCat(null); setBusy(true);
    try { await api.delete(`/admin/categories/${kind}/${c.id}`); await reload(); toast.success("Deleted"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onDrop = async (toIdx) => {
    setOverIdx(null);
    if (dragIdx === null || dragIdx === toIdx) { setDragIdx(null); return; }
    const next = [...items];
    const [moved] = next.splice(dragIdx, 1);
    const insertAt = toIdx > dragIdx ? toIdx - 1 : toIdx;
    next.splice(insertAt, 0, moved);
    setItems(next);
    setDragIdx(null);
    try { await api.post("/admin/categories/reorder", { kind, ids: next.map((c) => c.id) }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); reload(); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={`category-section-${kind}`}>
      <div className="font-heading font-bold mb-3">{label}</div>
      <div className="space-y-1.5 mb-3" onDragOver={(e) => e.preventDefault()}>
        {items.length === 0 && <div className="text-sm text-muted-foreground">No categories yet.</div>}
        {items.map((c, i) => (
          <Fragment key={c.id}>
            <div className={`h-0.5 -my-0.5 rounded-full transition-all ${overIdx === i && dragIdx !== null ? "bg-primary shadow-[0_0_6px_1px] shadow-primary/60" : "bg-transparent"}`} data-testid={overIdx === i && dragIdx !== null ? `category-dropline-${kind}` : undefined} />
            <div draggable={editId !== c.id}
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => { e.preventDefault(); if (dragIdx !== null) setOverIdx(i); }}
              onDrop={() => onDrop(i)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              className={`flex items-center gap-2 rounded-lg border bg-background px-2 py-1.5 transition-all ${dragIdx === i ? "opacity-40 ring-1 ring-primary/40 shadow-lg" : dragIdx !== null ? "opacity-70 border-border" : "border-border"} ${c.hidden ? "opacity-70" : ""}`}
              data-testid={`category-row-${kind}-${c.id}`}>
              <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab shrink-0" data-testid={`category-drag-${kind}-${c.id}`} />
              {editId === c.id ? (
                <>
                  <Input value={editVal} onChange={(e) => setEditVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveEdit(c.id); if (e.key === "Escape") setEditId(null); }} autoFocus className="h-8" data-testid={`category-edit-input-${kind}-${c.id}`} />
                  <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => saveEdit(c.id)} data-testid={`category-edit-save-${kind}-${c.id}`}><Check className="w-4 h-4 text-primary" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setEditId(null)}><X className="w-4 h-4" /></Button>
                </>
              ) : (
                <>
                  <span className="flex-1 min-w-0 truncate text-sm">{c.name}</span>
                  <span className="text-[11px] text-muted-foreground shrink-0" data-testid={`category-used-${kind}-${c.id}`} title="Items using this category">{c.used_count} in use</span>
                  <div className="flex items-center gap-1 shrink-0" title={c.hidden ? "Hidden from forms & filters" : "Visible"}>
                    {c.hidden ? <EyeOff className="w-3.5 h-3.5 text-muted-foreground" /> : <Eye className="w-3.5 h-3.5 text-muted-foreground" />}
                    <Switch checked={!c.hidden} onCheckedChange={() => toggleHidden(c)} data-testid={`category-visible-${kind}-${c.id}`} />
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => { setEditId(c.id); setEditVal(c.name); }} data-testid={`category-edit-${kind}-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive hover:text-destructive" onClick={() => setDelCat(c)} data-testid={`category-remove-${kind}-${c.id}`} disabled={busy}><Trash2 className="w-4 h-4" /></Button>
                </>
              )}
            </div>
          </Fragment>
        ))}
        <div onDragOver={(e) => { e.preventDefault(); if (dragIdx !== null) setOverIdx(items.length); }} onDrop={() => onDrop(items.length)} className="h-3 flex items-center">
          <div className={`h-0.5 w-full rounded-full transition-all ${overIdx === items.length && dragIdx !== null ? "bg-primary shadow-[0_0_6px_1px] shadow-primary/60" : "bg-transparent"}`} />
        </div>
      </div>
      <div className="flex gap-2">
        <Input value={nv} onChange={(e) => setNv(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder="Add category" data-testid={`category-input-${kind}`} />
        <Button variant="outline" onClick={add} disabled={busy} data-testid={`category-add-${kind}`}><Plus className="w-4 h-4" /></Button>
      </div>

      <AlertDialog open={!!delCat} onOpenChange={(o) => !o && setDelCat(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">{delCat?.used_count > 0 && <AlertTriangle className="w-5 h-5 text-amber-500" />}Delete "{delCat?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {delCat?.used_count > 0
                ? `${delCat.used_count} item${delCat.used_count === 1 ? "" : "s"} currently use this category. Deleting it won't remove those items — they'll be marked "Uncategorized" and this option will no longer appear. Consider hiding it instead.`
                : "This category isn't used by any content. It will be removed from the forms and Rider filters."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`category-confirm-delete-${kind}`}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function CategoryManager() {
  const [cats, setCats] = useState(null);

  const reload = async () => {
    try { const { data } = await api.get("/admin/categories"); setCats(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { reload(); }, []);

  if (!cats) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="category-manager">
      <div className="flex items-center gap-2">
        <Tags className="w-5 h-5 text-primary" />
        <div>
          <h2 className="font-heading text-lg font-bold">Content categories</h2>
          <p className="text-sm text-muted-foreground">Drag to reorder, rename inline, hide from forms, or delete. Hidden categories stay off the content forms and Rider filters. Changes save instantly.</p>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {KINDS.map(([k, label]) => (
          <KindSection key={k} kind={k} label={label} rows={cats[k] || []} reload={reload} />
        ))}
      </div>
    </div>
  );
}
